import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Plus, Minus, Calendar, X, Save, MapPin } from "lucide-react";

const daysOfWeek = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

export default function RoomAvailabilityForm({ room, onSave, onClose }) {
  const defaultHours = {
    monday: { is_available: true, start_time: "09:00", end_time: "17:00", break_times: [] },
    tuesday: { is_available: true, start_time: "09:00", end_time: "17:00", break_times: [] },
    wednesday: { is_available: true, start_time: "09:00", end_time: "17:00", break_times: [] },
    thursday: { is_available: true, start_time: "09:00", end_time: "17:00", break_times: [] },
    friday: { is_available: true, start_time: "09:00", end_time: "17:00", break_times: [] },
    saturday: { is_available: false, start_time: "09:00", end_time: "13:00", break_times: [] },
    sunday: { is_available: false, start_time: "09:00", end_time: "13:00", break_times: [] },
  };

  const [formData, setFormData] = useState({
    operating_hours: defaultHours,
    booking_advance_days: 30,
    booking_buffer_minutes: 15,
    special_requirements: "",
  });

  // Fetch room availability data
  const { data: availabilityData, isLoading } = useQuery({
    queryKey: ["roomAvailability", room?.id],
    queryFn: () => apiClient.get(`/api/settings/rooms/${room.id}/availability`),
    enabled: !!room?.id,
  });

  // Transform backend availability data to frontend format
  useEffect(() => {
    if (availabilityData && Array.isArray(availabilityData)) {
      const dayMap = {
        0: "sunday",
        1: "monday", 
        2: "tuesday",
        3: "wednesday",
        4: "thursday",
        5: "friday",
        6: "saturday",
      };

      const operating_hours = { ...defaultHours };
      
      availabilityData.forEach((availability) => {
        const dayName = dayMap[availability.dayOfWeek];
        if (dayName) {
          // Transform break times from camelCase to snake_case
          const transformedBreakTimes = (availability.breakTimes || []).map(breakTime => ({
            start_time: breakTime.startTime,
            end_time: breakTime.endTime,
          }));
          
          operating_hours[dayName] = {
            is_available: availability.status === "active",
            start_time: availability.startTime,
            end_time: availability.endTime,
            break_times: transformedBreakTimes,
          };
        }
      });

      setFormData(prev => ({
        ...prev,
        operating_hours,
      }));
    }
  }, [availabilityData]);

  // Save availability mutation
  const saveAvailabilityMutation = useMutation({
    mutationFn: (data) => 
      apiClient.put(`/api/settings/rooms/${room.id}/availability`, data),
  });

  const handleOperatingHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBreakTimeChange = (day, breakIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          break_times: prev.operating_hours[day].break_times.map((breakTime, index) =>
            index === breakIndex ? { ...breakTime, [field]: value } : breakTime
          ),
        },
      },
    }));
  };

  const addBreakTime = (day) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          break_times: [...prev.operating_hours[day].break_times, { start_time: "12:00", end_time: "13:00" }],
        },
      },
    }));
  };

  const removeBreakTime = (day, breakIndex) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...prev.operating_hours[day],
          break_times: prev.operating_hours[day].break_times.filter((_, index) => index !== breakIndex),
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await saveAvailabilityMutation.mutateAsync({
        operating_hours: formData.operating_hours,
      });
      
      // Call the parent's onSave callback to handle UI updates
      onSave({ ...room, ...formData });
      onClose();
    } catch (error) {
      console.error("Error saving room availability:", error);
      // Could add toast notification here
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Room Availability Settings - {room?.name}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Operating Hours */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Operating Hours
              </h3>
              <div className="grid gap-4">
                {daysOfWeek.map((day) => (
                  <div
                    key={day.key}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="w-24 font-medium">{day.label}</div>
                    <Switch
                      checked={formData.operating_hours[day.key].is_available}
                      onCheckedChange={(checked) =>
                        handleOperatingHoursChange(
                          day.key,
                          "is_available",
                          checked
                        )
                      }
                      data-testid={`room-avail-${day.key}-switch`}
                    />
                    {formData.operating_hours[day.key].is_available && (
                      <>
                        <div className="flex items-center gap-2">
                          <Label>From:</Label>
                          <Input
                            type="time"
                            value={formData.operating_hours[day.key].start_time}
                            onChange={(e) =>
                              handleOperatingHoursChange(
                                day.key,
                                "start_time",
                                e.target.value
                              )
                            }
                            className="w-32"
                            data-testid={`room-avail-${day.key}-start`}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label>To:</Label>
                          <Input
                            type="time"
                            value={formData.operating_hours[day.key].end_time}
                            onChange={(e) =>
                              handleOperatingHoursChange(
                                day.key,
                                "end_time",
                                e.target.value
                              )
                            }
                            className="w-32"
                            data-testid={`room-avail-${day.key}-end`}
                          />
                        </div>
                        
                        {/* Break Times */}
                        <div className="w-full space-y-2 mt-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Break Times:</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addBreakTime(day.key)}
                              className="text-xs h-7"
                              data-testid={`room-avail-${day.key}-add-break`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Break
                            </Button>
                          </div>
                          {formData.operating_hours[day.key].break_times.map((breakTime, breakIndex) => (
                            <div key={breakIndex} className="flex items-center gap-2">
                              <Input
                                type="time"
                                value={breakTime.start_time}
                                onChange={(e) =>
                                  handleBreakTimeChange(
                                    day.key,
                                    breakIndex,
                                    "start_time",
                                    e.target.value
                                  )
                                }
                                className="w-28"
                                data-testid={`room-avail-${day.key}-break-${breakIndex}-start`}
                              />
                              <span className="text-sm text-slate-500">to</span>
                              <Input
                                type="time"
                                value={breakTime.end_time}
                                onChange={(e) =>
                                  handleBreakTimeChange(
                                    day.key,
                                    breakIndex,
                                    "end_time",
                                    e.target.value
                                  )
                                }
                                className="w-28"
                                data-testid={`room-avail-${day.key}-break-${breakIndex}-end`}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeBreakTime(day.key, breakIndex)}
                                className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                                data-testid={`room-avail-${day.key}-break-${breakIndex}-remove`}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Booking Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Booking Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="booking_advance_days">
                    Booking Advance Days
                  </Label>
                  <Input
                    id="booking_advance_days"
                    type="number"
                    value={formData.booking_advance_days}
                    onChange={(e) =>
                      handleInputChange(
                        "booking_advance_days",
                        parseInt(e.target.value)
                      )
                    }
                    min="1"
                    max="365"
                  />
                  <p className="text-xs text-slate-500">
                    How many days in advance patients can book this room
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking_buffer_minutes">
                    Buffer Time (minutes)
                  </Label>
                  <Input
                    id="booking_buffer_minutes"
                    type="number"
                    value={formData.booking_buffer_minutes}
                    onChange={(e) =>
                      handleInputChange(
                        "booking_buffer_minutes",
                        parseInt(e.target.value)
                      )
                    }
                    min="0"
                    max="60"
                  />
                  <p className="text-xs text-slate-500">
                    Buffer time between appointments
                  </p>
                </div>
              </div>
            </div>


            {/* Special Requirements */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                Special Requirements & Guidelines
              </h3>
              <Textarea
                value={formData.special_requirements}
                onChange={(e) =>
                  handleInputChange("special_requirements", e.target.value)
                }
                placeholder="Any special requirements, usage guidelines, or restrictions for this room..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-6">
              <Button type="button" variant="outline" onClick={onClose} data-testid="room-avail-cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={isLoading || saveAvailabilityMutation.isPending}
                data-testid="room-avail-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saveAvailabilityMutation.isPending
                  ? "Saving..."
                  : "Save Room Availability Settings"
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
